import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-900 mb-2">文章不存在</h2>
          <p className="text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
          <Link
            to="/"
            className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-block"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '萝莉次元',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://lolidimension.com/article/${article.id}`,
    },
  };

  return (
    <>
      <JsonLd data={articleSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category?.name || article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-6">
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {category?.name || article.category}
              </Link>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
              <span className="text-xs text-foreground-300">·</span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
            </div>

            <h1 className="font-heading text-xl md:text-3xl text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>

            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xs text-primary-600 font-medium">{article.author[0]}</span>
              </div>
              <div>
                <p className="text-sm text-foreground-800 font-medium">{article.author}</p>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="article-content max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200/70">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={related.imageUrl}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2">
                        {related.date} · {related.readTime}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}